import { DATA_SOURCES } from '@/config/constants';
import { isDataSupported } from '../utils';
import { useCurrentCountry } from 'utils/hooks';
import { useEffect } from 'react';
import usePopularity from 'utils/hooks/usePopularity';
import usePopularityRelatedEntities from 'utils/hooks/usePopularityRelatedEntities';
import { v5 as uuidv5 } from 'uuid';

export const useOverviewWatchlistData = ({
    brandIds,
    categoryIds,
    relatedCategoryCategoryIds,
    relatedCategoryBrandIds,
    defaultInitialSortOption,
    placeholderDataByType,
}) => {
    const country = useCurrentCountry();

    const overviewBrandsDataQuery = usePopularity({
        paginateBy: 'brands',
        brands: brandIds,
        countries: country ? [country.id] : null,
        enabled: !!(country && brandIds?.length),
        order_by: defaultInitialSortOption.sortBy,
        ascending: defaultInitialSortOption.ascending,
        placeholderData: placeholderDataByType.brand.map((d) => d),
    });

    const overviewCategoriesDataQuery = usePopularity({
        paginateBy: 'categories',
        categories: categoryIds,
        countries: country ? [country.id] : null,
        enabled: !!(country && categoryIds?.length),
        order_by: defaultInitialSortOption.sortBy,
        ascending: defaultInitialSortOption.ascending,
        placeholderData: placeholderDataByType.category.filter(
            (d) => d.category,
        ),
    });

    const sharedQueryData = {
        paginateBy: 'categories',
        countries: country ? [country.id] : null,
        order_by: defaultInitialSortOption?.sortBy,
        ascending: defaultInitialSortOption?.ascending,
        includeParentId: true,
        keepPreviousData: false,
        fetch_in_pairs: true,
    };

    const { query: relatedCategoryCategoriesDataQuery } =
        usePopularityRelatedEntities({
            ...sharedQueryData,
            enabled: !!(relatedCategoryCategoryIds?.length && country),
            categories:
                relatedCategoryCategoryIds?.map((r) => r.categoryId) || null,
            related_categories:
                relatedCategoryCategoryIds?.map((r) => r.relatedCategoryId) ||
                null,
            placeholderData: placeholderDataByType.relatedCategoryCategory.map(
                (d) => d,
            ),
        });
    useEffect(() => {
        if (
            !relatedCategoryCategoriesDataQuery.hasNextPage ||
            relatedCategoryCategoriesDataQuery.isFetchingNextPage
        )
            return;
        relatedCategoryCategoriesDataQuery.fetchNextPage();
    }, [
        relatedCategoryCategoriesDataQuery.hasNextPage,
        relatedCategoryCategoriesDataQuery.isFetchingNextPage,
        relatedCategoryCategoriesDataQuery.fetchNextPage,
    ]);
    const allRelatedCategoriesCategories = (
        relatedCategoryCategoriesDataQuery.data?.pages || []
    ).flatMap((page) =>
        (page.data || []).map((i) => ({
            ...i,
            categoryId: i.parentId,
            relatedCategoryId: i.id,
            id: uuidv5(i.parentId, i.id),
            type: 'related-category',
            exportName: `${i.parentTrendName} - ${i.name}`,
            relatedName: i.parentTrendName, // Use parent trend name for related categories as root name
        })),
    );

    const { query: relatedCategoryBrandsDataQuery } =
        usePopularityRelatedEntities({
            ...sharedQueryData,
            enabled: !!(relatedCategoryBrandIds?.length && country),
            brands: relatedCategoryBrandIds?.map((r) => r.brandId) || null,
            related_categories:
                relatedCategoryBrandIds?.map((r) => r.relatedCategoryId) ||
                null,
            placeholderData: placeholderDataByType.relatedCategoryBrand.map(
                (d) => d,
            ),
        });
    useEffect(() => {
        if (
            !relatedCategoryBrandsDataQuery.hasNextPage ||
            relatedCategoryBrandsDataQuery.isFetchingNextPage
        )
            return;
        relatedCategoryBrandsDataQuery.fetchNextPage();
    }, [
        relatedCategoryBrandsDataQuery.hasNextPage,
        relatedCategoryBrandsDataQuery.isFetchingNextPage,
        relatedCategoryBrandsDataQuery.fetchNextPage,
    ]);
    const allRelatedCategoriesBrands = (
        relatedCategoryBrandsDataQuery.data?.pages || []
    ).flatMap((page) =>
        (page.data || []).map((i) => ({
            ...i,
            brandId: i.parentId,
            relatedCategoryId: i.id,
            id: uuidv5(i.parentId, i.id),
            type: 'related-category',
            exportName: `${i.parentTrendName} - ${i.name}`,
            relatedName: i.parentTrendName, // Use parent trend name for related categories as root name
        })),
    );

    const allOverviewData = [
        ...(overviewBrandsDataQuery.data || []),
        ...(overviewCategoriesDataQuery.data || []),
        ...(allRelatedCategoriesCategories || []),
        ...(allRelatedCategoriesBrands || []),
    ].filter((d) => isDataSupported(d, country, DATA_SOURCES.POPULARITY));

    return {
        overviewBrandsDataQuery,
        overviewCategoriesDataQuery,
        relatedCategoryCategoriesDataQuery,
        relatedCategoryBrandsDataQuery,
        allOverviewData,
        allRelatedCategoriesCategories,
        allRelatedCategoriesBrands,
    };
};
